import React, { useState } from 'react';
import { BellIcon, BuildingIcon, CheckIcon, CreditCardIcon, LockIcon, StoreIcon, TruckIcon, WalletIcon } from 'lucide-react';
const fieldClass = 'w-full border border-white/10 bg-ink-900 px-4 py-3 text-sm text-white placeholder:text-white/30 focus:border-pink-500 focus:outline-none';
const labelClass = 'block text-[10px] font-bold uppercase tracking-[0.18em] text-white/45';
const PAYMENT_METHODS = [{
  id: 'card',
  label: 'Tarjeta de crédito/débito',
  hint: 'Visa, Mastercard, OCA, Cabal',
  icon: CreditCardIcon
}, {
  id: 'mp',
  label: 'MercadoPago',
  hint: 'Checkout con cuotas sin interés',
  icon: WalletIcon
}, {
  id: 'transfer',
  label: 'Transferencia bancaria',
  hint: 'BROU, Itaú, Santander',
  icon: BuildingIcon
}];
const NOTIFICATIONS = [{
  id: 'new-order',
  label: 'Nuevo pedido',
  hint: 'Email inmediato cada vez que entra un pedido.'
}, {
  id: 'low-stock',
  label: 'Stock bajo',
  hint: 'Aviso cuando un producto queda con 8 unidades o menos.'
}, {
  id: 'new-client',
  label: 'Nuevo cliente',
  hint: 'Resumen diario de las cuentas creadas.'
}, {
  id: 'weekly',
  label: 'Reporte semanal de ventas',
  hint: 'Todos los lunes a las 9:00.'
}];
function Toggle({
  checked,
  onChange,
  label
}: {
  checked: boolean;
  onChange: () => void;
  label: string;
}) {
  return <button type="button" role="switch" aria-checked={checked} aria-label={label} onClick={onChange} className={`relative h-6 w-11 shrink-0 rounded-full transition-colors ${checked ? 'bg-pink-500' : 'bg-ink-500'}`} data-id="element-511" data-mp="14zmgc2867y5f">
      <span className={`absolute top-1 h-4 w-4 rounded-full bg-white transition-all ${checked ? 'left-6' : 'left-1'}`} data-id="element-512" data-mp="100je0pjks615" />
    </button>;
}
function Panel({
  icon: Icon,
  title,
  description,
  children
}: {
  icon: React.ComponentType<{
    className?: string;
  }>;
  title: string;
  description: string;
  children: React.ReactNode;
}) {
  return <section className="border border-white/8 bg-ink-700" data-id="element-513" data-mp="2nzdd762aqp4z">
      <div className="flex items-start gap-4 border-b border-white/8 p-5" data-id="element-514" data-mp="2nxskzn3gfiaa">
        <span className="flex h-10 w-10 shrink-0 items-center justify-center bg-pink-500/12" data-id="element-515" data-mp="2j0scl5j4u3g0">
          <Icon className="h-[18px] w-[18px] text-pink-500" data-id="element-516" data-mp="2e1z7o1hwvkrg" />
        </span>
        <div data-id="element-517" data-mp="3bp7ohj6yw5n4">
          <h2 className="font-display text-xl uppercase tracking-[0.14em] text-white" data-id="element-518" data-mp="3bnmv3wwe2squ">
            {title}
          </h2>
          <p className="mt-1 text-xs text-white/40" data-id="element-519" data-mp="36q3guwzoyfyw">{description}</p>
        </div>
      </div>
      <div className="p-5" data-id="element-520" data-mp="31xk23wp9pesi">{children}</div>
    </section>;
}
export function SettingsSection() {
  const [payments, setPayments] = useState<Record<string, boolean>>({
    card: true,
    mp: true,
    transfer: false
  });
  const [notifications, setNotifications] = useState<Record<string, boolean>>({
    'new-order': true,
    'low-stock': true,
    'new-client': false,
    weekly: true
  });
  const [freeShipping, setFreeShipping] = useState(3500);
  const [saved, setSaved] = useState(false);
  const [passwordMessage, setPasswordMessage] = useState('');
  const handleSave = (e: React.FormEvent) => {
    e.preventDefault();
    setSaved(true);
    window.setTimeout(() => setSaved(false), 2200);
  };
  return <form onSubmit={handleSave} className="space-y-6" data-id="element-521" data-mp="fss2x20wqds7">
      <Panel icon={StoreIcon} title="Datos de la tienda" description="Aparecen en el sitio, los emails y las facturas." data-id="element-522" data-mp="krd1kx0igpej">
        <div className="grid gap-5 sm:grid-cols-2" data-id="element-523" data-mp="oq2g4sxacczr">
          <div data-id="element-524" data-mp="tjwstceh3rpc">
            <label className={labelClass} htmlFor="s-name" data-id="element-525" data-mp="tjf0uryufzn8">
              Nombre de la tienda
            </label>
            <input id="s-name" defaultValue="ZADAR" className={`mt-2 ${fieldClass}`} data-id="element-526" data-mp="yh6qffc0n9o9" />
          </div>
          <div data-id="element-527" data-mp="18fct3z5ncju6">
            <label className={labelClass} htmlFor="s-email" data-id="element-528" data-mp="18gxn9no1u0ri">
              Email de contacto
            </label>
            <input id="s-email" type="email" defaultValue="hola@zadar.uy" className={`mt-2 ${fieldClass}`} data-id="element-529" data-mp="255ygwumb7p4d" />
          </div>
          <div data-id="element-530" data-mp="2ew18cqds8v0l">
            <label className={labelClass} htmlFor="s-phone" data-id="element-531" data-mp="2ewj9o1faxzfd">
              Teléfono
            </label>
            <input id="s-phone" type="tel" defaultValue="+598 99 123 456" className={`mt-2 ${fieldClass}`} data-id="element-532" data-mp="2a0cf14scwacq" />
          </div>
          <div data-id="element-533" data-mp="1hbex52s24fki">
            <label className={labelClass} htmlFor="s-address" data-id="element-534" data-mp="1hbx1kmovi03i">
              Dirección
            </label>
            <input id="s-address" defaultValue="Bvar. Artigas 1234, Montevideo" className={`mt-2 ${fieldClass}`} data-id="element-535" data-mp="1v70ld7qguvbv" />
          </div>
        </div>
      </Panel>

      <Panel icon={TruckIcon} title="Envíos" description="Definí desde qué monto el envío es gratis." data-id="element-536" data-mp="32n6wrtxsgvmi">
        <div className="grid gap-5 sm:grid-cols-2" data-id="element-537" data-mp="3a5egodoj85ab">
          <div data-id="element-538" data-mp="3f5j0odonfi5a">
            <label className={labelClass} htmlFor="s-free" data-id="element-539" data-mp="3f596sf0hgqub">
              Mínimo para envío gratis
            </label>
            <div className="mt-2 flex items-center border border-white/10 bg-ink-900 focus-within:border-pink-500" data-id="element-540" data-mp="3091mzfszlrfk">
              <span className="pl-4 text-sm text-white/35" data-id="element-541" data-mp="3584lsiidj1f3">$</span>
              <input id="s-free" type="number" min={0} step={100} value={freeShipping} onChange={e => setFreeShipping(Number(e.target.value))} className="w-full bg-transparent px-3 py-3 text-sm text-white focus:outline-none" data-id="element-542" data-mp="3swe3b88k130f" />
            </div>
            <p className="mt-2 text-xs text-white/35" data-id="element-543" data-mp="3o3urafpimfxq">
              Actualmente el envío es gratis desde ${' '}
              {freeShipping.toLocaleString('es-UY')} en todo Uruguay.
            </p>
          </div>
          <div data-id="element-544" data-mp="ucuk11a0nav9">
            <label className={labelClass} htmlFor="s-cost" data-id="element-545" data-mp="udcogl6u0ve9">
              Costo de envío estándar
            </label>
            <div className="mt-2 flex items-center border border-white/10 bg-ink-900 focus-within:border-pink-500" data-id="element-546" data-mp="pfb5s1dbiy3b">
              <span className="pl-4 text-sm text-white/35" data-id="element-547" data-mp="lav3sb6tsatd">$</span>
              <input id="s-cost" type="number" defaultValue={290} className="w-full bg-transparent px-3 py-3 text-sm text-white focus:outline-none" data-id="element-548" data-mp="ge4kzg3mz149" />
            </div>
            <p className="mt-2 text-xs text-white/35" data-id="element-549" data-mp="6lfbgqrnbxbk">
              Se aplica a pedidos por debajo del mínimo.
            </p>
          </div>
        </div>
      </Panel>

      <Panel icon={CreditCardIcon} title="Métodos de pago" description="Activá o desactivá las opciones visibles en el checkout." data-id="element-550" data-mp="3ttj0u6h1umyk">
        <ul className="space-y-3" data-id="element-551" data-mp="3jubrdudfrcgq">
          {PAYMENT_METHODS.map(({
          id,
          label,
          hint,
          icon: Icon
        }) => <li key={id} className={`flex items-center gap-4 border p-4 transition-colors ${payments[id] ? 'border-pink-500/40 bg-pink-500/5' : 'border-white/8 bg-ink-800'}`} data-id="element-552" data-mp="1zcj0knbhfgho">
              <Icon className={`h-5 w-5 shrink-0 ${payments[id] ? 'text-pink-500' : 'text-white/35'}`} data-id="element-553" data-mp="2dbiplw2wpxed" />
              <div className="min-w-0 flex-1" data-id="element-554" data-mp="1gjf4ywepetkf">
                <p className="text-sm font-semibold text-white" data-id="element-555" data-mp="1ljtekivyebkk">{label}</p>
                <p className="text-xs text-white/40" data-id="element-556" data-mp="1pmg9isnx8clz">{hint}</p>
              </div>
              <Toggle checked={payments[id]} label={label} onChange={() => setPayments(prev => ({
            ...prev,
            [id]: !prev[id]
          }))} data-id="element-557" data-mp="1pkd9rgwyv542" />
            </li>)}
        </ul>
      </Panel>

      <Panel icon={BellIcon} title="Preferencias de notificación" description="Elegí qué avisos recibe el equipo de administración." data-id="element-558" data-mp="11usrz1cjudwd">
        <ul className="divide-y divide-white/5" data-id="element-559" data-mp="3mgnnqksx94mg">
          {NOTIFICATIONS.map(({
          id,
          label,
          hint
        }) => <li key={id} className="flex items-center gap-4 py-4 first:pt-0 last:pb-0" data-id="element-560" data-mp="588sr21zs8md">
              <div className="min-w-0 flex-1" data-id="element-561" data-mp="7cogndqakgf">
                <p className="text-sm font-semibold text-white" data-id="element-562" data-mp="aqt629v75r1">{label}</p>
                <p className="text-xs text-white/40" data-id="element-563" data-mp="e6ko1hdszul5">{hint}</p>
              </div>
              <Toggle checked={notifications[id]} label={label} onChange={() => setNotifications(prev => ({
            ...prev,
            [id]: !prev[id]
          }))} data-id="element-564" data-mp="98autcbn3426" />
            </li>)}
        </ul>
      </Panel>

      <Panel icon={LockIcon} title="Cambiar contraseña" description="Recomendamos actualizarla cada 6 meses." data-id="element-565" data-mp="wzolzey20tlk">
        <div className="grid gap-5 sm:grid-cols-3" data-id="element-566" data-mp="suyus3ecc36z">
          <div data-id="element-567" data-mp="1bmeyzdl54amz">
            <label className={labelClass} htmlFor="s-current" data-id="element-568" data-mp="1blvgnvrkvk8f">
              Contraseña actual
            </label>
            <input id="s-current" type="password" placeholder="••••••••" autoComplete="current-password" className={`mt-2 ${fieldClass}`} data-id="element-569" data-mp="1gjnhjfa7agor" />
          </div>
          <div data-id="element-570" data-mp="sv498zr5i3wo">
            <label className={labelClass} htmlFor="s-new" data-id="element-571" data-mp="sum4tfuc4jdo">
              Nueva contraseña
            </label>
            <input id="s-new" type="password" placeholder="Mínimo 8 caracteres" autoComplete="new-password" className={`mt-2 ${fieldClass}`} data-id="element-572" data-mp="xmmazq1008ip" />
          </div>
          <div data-id="element-573" data-mp="ntoqjecg49hg">
            <label className={labelClass} htmlFor="s-repeat" data-id="element-574" data-mp="9w03q0ytis9r">
              Repetir contraseña
            </label>
            <input id="s-repeat" type="password" placeholder="••••••••" autoComplete="new-password" className={`mt-2 ${fieldClass}`} data-id="element-575" data-mp="eutgfr6a6f8v" />
          </div>
        </div>
        <div className="mt-5 flex flex-wrap items-center gap-4" data-id="element-576" data-mp="1w0snncjhzbtk">
          <button type="button" onClick={() => {
          setPasswordMessage('Contraseña actualizada correctamente.');
          window.setTimeout(() => setPasswordMessage(''), 2200);
        }} className="border border-white/20 px-6 py-3 text-[11px] font-bold uppercase tracking-[0.16em] text-white/70 transition-colors hover:border-pink-500 hover:text-white" data-id="element-577" data-mp="1i09kojtw2vy9">
            Actualizar contraseña
          </button>
          {passwordMessage && <p role="status" className="flex items-center gap-2 text-xs text-teal-400" data-id="element-578" data-mp="25wdkyxdmfnwz">
              <CheckIcon className="h-3.5 w-3.5" data-id="element-579" data-mp="2tlopmpy3e2ge" />
              {passwordMessage}
            </p>}
        </div>
      </Panel>

      <div className="flex flex-wrap items-center justify-end gap-4 border border-white/8 bg-ink-700 p-5" data-id="element-580" data-mp="myr0kzvgn49b">
        {saved && <p role="status" className="flex items-center gap-2 text-xs text-teal-400" data-id="element-581" data-mp="i0fqco196rqk">
            <CheckIcon className="h-4 w-4" data-id="element-582" data-mp="vrkkjaluqrti" /> Cambios guardados
          </p>}
        <button type="button" className="border border-white/20 px-6 py-3 text-[11px] font-bold uppercase tracking-[0.16em] text-white/70 transition-colors hover:border-white hover:text-white" data-id="element-583" data-mp="rppmcez7wujy">
          Descartar
        </button>
        <button type="submit" className="bg-pink-500 px-6 py-3 text-[11px] font-bold uppercase tracking-[0.16em] text-white transition-colors hover:bg-pink-600" data-id="element-584" data-mp="3v80pmr8i14n7">
          Guardar cambios
        </button>
      </div>
    </form>;
}